import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Link, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {FormattedMessage, useIntl} from 'react-intl';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';

import {IStore} from 'redux/interface';
import {RoutesUrl} from 'utils/constants/routeConstants';
import {getProgramDetail, getStepContent, interventionAsyncActions} from 'redux/pageAsyncActions';
import {completeStepApi, getStepDraftApi, saveStepDraftApi} from 'redux/api/patientHome';
import {IStepContentData, StepContentType, StepKind} from 'redux/intervention/interfaces';
import Loader from 'components/loader';
import {useStepContentSse} from 'utils/hooks/useStepContentSse';

import './styles.scss';

// ── Renderer contract ─────────────────────────────────────────────────────────

interface RendererProps {
  content: IStepContentData
  draftPayload: Record<string, unknown>
  onPayloadChange: (payload: Record<string, unknown>) => void
  isReadOnly: boolean
  fieldErrors: Record<string, string>
  apId: number
}

// ── Submit labels per content type ────────────────────────────────────────────

const SUBMIT_LABELS: Record<StepContentType, string> = {
  form: 'Submit',
  lesson: 'Mark as read',
  activity: 'Continue',
  education: 'Continue',
  ai_assistant: 'Save',
  medication: 'Save',
  xpedition: 'Mark as done'
};

// ── Submit payload builder ────────────────────────────────────────────────────

function buildSubmitPayload(contentType: StepContentType, draft: Record<string, unknown>): Record<string, unknown> {
  switch (contentType) {
    case 'lesson':
    case 'medication':
    case 'xpedition':
      return {acknowledged_at: new Date().toISOString()};
    default:
      return draft;
  }
}

// ── Client-side validation ────────────────────────────────────────────────────

type AnswerEntry = Record<string, unknown>;

function validateFormAnswers(
  payload: Record<string, unknown>,
  contentPayload: Record<string, unknown>
): Record<string, string> {
  const errors: Record<string, string> = {};

  type FormQ = {id: string; required: boolean};
  const questions = (contentPayload.questions as FormQ[] | undefined) ?? [];
  const answers = (payload.answers as AnswerEntry[] | undefined) ?? [];
  const answersMap = answers.reduce<Record<string, unknown>>(
    (acc, a) => ({...acc, [String(a.question_id)]: a.value}),
    {}
  );

  questions.filter(q => q.required).forEach(q => {
    const v = answersMap[q.id];
    const isEmpty = v == null || v === '' || (Array.isArray(v) && v.length === 0);

    if (isEmpty) {
      errors[`answers.${q.id}`] = 'This field is required';
    }
  });

  return errors;
}

function validatePayload(
  contentType: StepContentType,
  payload: Record<string, unknown>,
  contentPayload: Record<string, unknown>
): Record<string, string> {
  if (contentType === 'form') {
    return validateFormAnswers(payload, contentPayload);
  }

  const errors: Record<string, string> = {};

  if (contentType === 'activity' && !payload.completion) {
    errors.completion = 'Please select a completion status';
  }

  if (contentType === 'medication' && !payload.acknowledged) {
    errors.acknowledged = 'Please confirm you understand your medication instructions';
  }

  if (contentType === 'education') {
    const mediaType = String(contentPayload.media_type ?? '');

    if ((mediaType === 'video' || mediaType === 'audio') && Number(payload.watched_pct ?? 0) < 80) {
      errors.watched_pct = 'Please watch at least 80% of the content before continuing';
    }
  }

  return errors;
}

// ── Form renderer ─────────────────────────────────────────────────────────────

type FormQuestionType = 'single_choice' | 'multi_choice' | 'scale' | 'number' | 'text' | 'date';

interface FormQuestion {
  id: string
  text: string
  type: FormQuestionType
  required: boolean
  options?: Array<{id: string; label: string}>
  min?: number
  max?: number
  minLabel?: string
  maxLabel?: string
  placeholder?: string
}

type SetAnswer = (questionId: string, value: unknown) => void;

function renderSingleChoice(
  q: FormQuestion,
  answersMap: Record<string, unknown>,
  isReadOnly: boolean,
  onAnswer: SetAnswer
): React.ReactElement {
  return (
    <div className="form-question__options" role="radiogroup" aria-label={q.text}>
      {(q.options ?? []).map(opt => (
        <label key={opt.id} className="form-question__option">
          <input
            type="radio"
            name={q.id}
            value={opt.id}
            checked={answersMap[q.id] === opt.id}
            onChange={() => !isReadOnly && onAnswer(q.id, opt.id)}
            disabled={isReadOnly}
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

function renderMultiChoice(
  q: FormQuestion,
  answersMap: Record<string, unknown>,
  isReadOnly: boolean,
  onAnswer: SetAnswer
): React.ReactElement {
  const selected = (answersMap[q.id] as string[] | undefined) ?? [];

  return (
    <div className="form-question__options">
      {(q.options ?? []).map(opt => (
        <label key={opt.id} className="form-question__option">
          <input
            type="checkbox"
            value={opt.id}
            checked={selected.includes(opt.id)}
            onChange={(e) => {
              if (isReadOnly) {
                return;
              }
              const prev = [...selected];

              onAnswer(q.id, e.target.checked ? [...prev, opt.id] : prev.filter(id => id !== opt.id));
            }}
            disabled={isReadOnly}
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

// ── Per-type input helpers (each kept simple to stay below complexity limit) ──

type InputArgs = [FormQuestion, Record<string, unknown>, boolean, SetAnswer];

function renderScaleInput(q: FormQuestion, answersMap: Record<string, unknown>, isReadOnly: boolean, onAnswer: SetAnswer): React.ReactElement {
  return (
    <div className="form-question__scale">
      {q.minLabel && <span className="form-question__scale-label">{q.minLabel}</span>}
      <input
        type="range"
        min={q.min ?? 0}
        max={q.max ?? 10}
        value={String(answersMap[q.id] ?? q.min ?? 0)}
        onChange={(e) => !isReadOnly && onAnswer(q.id, Number(e.target.value))}
        disabled={isReadOnly}
      />
      <span className="form-question__scale-value">{String(answersMap[q.id] ?? '')}</span>
      {q.maxLabel && <span className="form-question__scale-label">{q.maxLabel}</span>}
    </div>
  );
}

function renderNumberInput(q: FormQuestion, answersMap: Record<string, unknown>, isReadOnly: boolean, onAnswer: SetAnswer): React.ReactElement {
  return (
    <input
      type="number"
      className="form-question__input"
      min={q.min}
      max={q.max}
      value={String(answersMap[q.id] ?? '')}
      onChange={(e) => !isReadOnly && onAnswer(q.id, e.target.value === '' ? null : Number(e.target.value))}
      disabled={isReadOnly}
    />
  );
}

function renderTextInput(q: FormQuestion, answersMap: Record<string, unknown>, isReadOnly: boolean, onAnswer: SetAnswer): React.ReactElement {
  return (
    <textarea
      className="form-question__textarea"
      placeholder={isReadOnly ? '' : (q.placeholder ?? '')}
      value={String(answersMap[q.id] ?? '')}
      onChange={(e) => !isReadOnly && onAnswer(q.id, e.target.value)}
      disabled={isReadOnly}
      rows={4}
    />
  );
}

function renderDateInput(q: FormQuestion, answersMap: Record<string, unknown>, isReadOnly: boolean, onAnswer: SetAnswer): React.ReactElement {
  return (
    <input
      type="date"
      className="form-question__input"
      value={String(answersMap[q.id] ?? '')}
      onChange={(e) => !isReadOnly && onAnswer(q.id, e.target.value)}
      disabled={isReadOnly}
    />
  );
}

const QUESTION_INPUT_REGISTRY: Partial<Record<FormQuestionType, (...args: InputArgs) => React.ReactElement>> = {
  single_choice: renderSingleChoice,
  multi_choice: renderMultiChoice,
  scale: renderScaleInput,
  number: renderNumberInput,
  text: renderTextInput,
  date: renderDateInput
};

interface FormQuestionInputProps {
  question: FormQuestion
  answersMap: Record<string, unknown>
  isReadOnly: boolean
  onAnswer: SetAnswer
}

const FormQuestionInput: React.FC<FormQuestionInputProps> = ({question: q, answersMap, isReadOnly, onAnswer}) => {
  const renderer = QUESTION_INPUT_REGISTRY[q.type];

  return renderer ? renderer(q, answersMap, isReadOnly, onAnswer) : null;
};

interface FormQuestionCardProps {
  question: FormQuestion
  answersMap: Record<string, unknown>
  isReadOnly: boolean
  fieldErrors: Record<string, string>
  onAnswer: SetAnswer
}

const FormQuestionCard: React.FC<FormQuestionCardProps> = ({question: q, answersMap, isReadOnly, fieldErrors, onAnswer}) => {
  const errorKey = `answers.${q.id}`;
  const hasError = Boolean(fieldErrors[errorKey]);

  return (
    <div className={`form-question${hasError ? ' form-question--error' : ''}`}>
      <label className="form-question__label">
        {q.text}
        {q.required && <span className="form-question__required" aria-label="required"> *</span>}
      </label>
      <FormQuestionInput question={q} answersMap={answersMap} isReadOnly={isReadOnly} onAnswer={onAnswer}/>
      {hasError && (
        <span className="form-question__error" role="alert">{fieldErrors[errorKey]}</span>
      )}
    </div>
  );
};

const FormRenderer: React.FC<RendererProps> = ({content, draftPayload, onPayloadChange, isReadOnly, fieldErrors}) => {
  const questions = (content.payload.questions as FormQuestion[] | undefined) ?? [];
  const answers = (draftPayload.answers as AnswerEntry[] | undefined) ?? [];
  const answersMap = answers.reduce<Record<string, unknown>>(
    (acc, a) => ({...acc, [String(a.question_id)]: a.value}),
    {}
  );

  const setAnswer: SetAnswer = (questionId, value) => {
    const updated = {...answersMap, [questionId]: value};

    onPayloadChange({
      answers: Object.entries(updated).map(([id, v]) => ({['question_id']: id, value: v}))
    });
  };

  const answered = questions.filter(q => q.required).filter(q => {
    const v = answersMap[q.id];

    if (Array.isArray(v)) {
      return v.length > 0;
    }

    return v != null && v !== '';
  }).length;
  const requiredCount = questions.filter(q => q.required).length;

  return (
    <div className="step-renderer step-renderer--form">
      {requiredCount > 0 && (
        <div className="step-renderer__progress">
          <FormattedMessage
            id="gritx.stepContent.formProgress"
            defaultMessage="{answered} of {total} required answered"
            values={{answered, total: requiredCount}}
          />
        </div>
      )}
      {questions.map(q => (
        <FormQuestionCard
          key={q.id}
          question={q}
          answersMap={answersMap}
          isReadOnly={isReadOnly}
          fieldErrors={fieldErrors}
          onAnswer={setAnswer}
        />
      ))}
    </div>
  );
};

// ── Lesson renderer ───────────────────────────────────────────────────────────

const LessonRenderer: React.FC<RendererProps> = ({content, onPayloadChange, isReadOnly}) => {
  const bodyMarkdown = String(content.payload.body_markdown ?? '');
  const media = (content.payload.media as Array<{url: string; type: 'image' | 'video'}> | undefined) ?? [];
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (isReadOnly || !scrollRef.current) {
      return;
    }
    const el = scrollRef.current;
    const scrollPct = el.scrollHeight > el.clientHeight
      ? el.scrollTop / (el.scrollHeight - el.clientHeight)
      : 1;

    onPayloadChange({scroll_position: scrollPct});
  };

  return (
    <div className="step-renderer step-renderer--lesson" ref={scrollRef} onScroll={handleScroll}>
      <pre className="lesson-body__markdown">{bodyMarkdown}</pre>
      {media.map((m, i) => (
        m.type === 'video'
          ? (
            <video key={i} className="lesson-media" src={m.url} controls>
              <track kind="captions"/>
            </video>
          )
          : <img key={i} className="lesson-media" src={m.url} alt=""/>
      ))}
    </div>
  );
};

// ── Activity renderer ─────────────────────────────────────────────────────────

const COMPLETION_OPTIONS = [
  {id: 'yes', label: 'Yes, completed'},
  {id: 'partial', label: 'Partially completed'},
  {id: 'no', label: 'Did not complete'}
];

const ActivityRenderer: React.FC<RendererProps> = ({content, draftPayload, onPayloadChange, isReadOnly, fieldErrors}) => {
  const instructions = String(content.payload.instructions_markdown ?? '');
  const completionPrompt = String(content.payload.completion_prompt ?? '');
  const completion = String(draftPayload.completion ?? '');
  const note = String(draftPayload.note ?? '');

  return (
    <div className="step-renderer step-renderer--activity">
      {instructions && <pre className="activity__instructions">{instructions}</pre>}
      {completionPrompt && <p className="activity__prompt">{completionPrompt}</p>}

      <div className={`activity__completion${fieldErrors.completion ? ' activity__completion--error' : ''}`}>
        <p className="activity__completion-label">
          <FormattedMessage id="gritx.stepContent.activityCompletion" defaultMessage="How did it go?"/>
        </p>
        {COMPLETION_OPTIONS.map((opt) => (
          <label key={opt.id} className="form-question__option">
            <input
              type="radio"
              name="completion"
              value={opt.id}
              checked={completion === opt.id}
              onChange={() => !isReadOnly && onPayloadChange({...draftPayload, completion: opt.id})}
              disabled={isReadOnly}
            />
            {opt.label}
          </label>
        ))}
        {fieldErrors.completion && <span className="form-question__error" role="alert">{fieldErrors.completion}</span>}
      </div>

      <div className="activity__note">
        <label className="activity__note-label">
          <FormattedMessage id="gritx.stepContent.activityNote" defaultMessage="Notes (optional)"/>
        </label>
        <textarea
          className="form-question__textarea"
          value={note}
          maxLength={2000}
          onChange={(e) => !isReadOnly && onPayloadChange({...draftPayload, note: e.target.value})}
          disabled={isReadOnly}
          rows={4}
        />
      </div>
    </div>
  );
};

// ── Education renderer ────────────────────────────────────────────────────────

interface EducationMediaProps {
  mediaType: string
  mediaUrl: string
  currentPct: number
  isReadOnly: boolean
  onProgress: (pct: number) => void
}

const EducationMedia: React.FC<EducationMediaProps> = ({mediaType, mediaUrl, currentPct, isReadOnly, onProgress}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const handleTimeUpdate = (el: HTMLVideoElement | HTMLAudioElement) => {
    if (isReadOnly) {
      return;
    }
    const pct = el.duration > 0 ? Math.round((el.currentTime / el.duration) * 100) : 0;

    onProgress(Math.max(currentPct, pct));
  };

  if (mediaType === 'video') {
    return (
      <video
        ref={videoRef}
        className="education-media"
        src={mediaUrl}
        controls
        onTimeUpdate={() => videoRef.current && handleTimeUpdate(videoRef.current)}
      >
        <track kind="captions"/>
      </video>
    );
  }

  if (mediaType === 'audio') {
    return (
      <audio
        ref={audioRef}
        className="education-media education-media--audio"
        src={mediaUrl}
        controls
        onTimeUpdate={() => audioRef.current && handleTimeUpdate(audioRef.current)}
      >
        <track kind="captions"/>
      </audio>
    );
  }

  if (mediaType === 'pdf') {
    return (
      <iframe
        className="education-media education-media--pdf"
        src={mediaUrl}
        title="PDF content"
        onLoad={() => !isReadOnly && onProgress(100)}
      />
    );
  }

  return null;
};

const EducationRenderer: React.FC<RendererProps> = ({content, draftPayload, onPayloadChange, isReadOnly, fieldErrors}) => {
  const mediaUrl = String(content.payload.media_url ?? '');
  const mediaType = String(content.payload.media_type ?? '');
  const description = String(content.payload.description_markdown ?? '');
  const watchedPct = Number(draftPayload.watched_pct ?? 0);
  const isMedia = mediaType === 'video' || mediaType === 'audio';

  return (
    <div className="step-renderer step-renderer--education">
      <EducationMedia
        mediaType={mediaType}
        mediaUrl={mediaUrl}
        currentPct={watchedPct}
        isReadOnly={isReadOnly}
        onProgress={(pct) => onPayloadChange({watched_pct: pct})}
      />
      {description && <pre className="education__description">{description}</pre>}
      {isMedia && watchedPct < 80 && !isReadOnly && (
        <p className={`education__watch-hint${fieldErrors.watched_pct ? ' education__watch-hint--error' : ''}`}>
          <FormattedMessage
            id="gritx.stepContent.watchProgress"
            defaultMessage="{pct}% — reach 80% to continue"
            values={{pct: watchedPct}}
          />
        </p>
      )}
    </div>
  );
};

// ── AI Assistant renderer ─────────────────────────────────────────────────────

const AiAssistantRenderer: React.FC<RendererProps> = ({content, isReadOnly}) => {
  const assistantId = String(content.payload.assistant_id ?? '');

  return (
    <div className="step-renderer step-renderer--ai-assistant">
      <p className="ai-assistant__description">
        <FormattedMessage
          id="gritx.stepContent.aiAssistantIntro"
          defaultMessage="Complete this step by chatting with your AI assistant."
        />
      </p>
      <div className="ai-assistant__placeholder">
        <span className="ai-assistant__id">{assistantId}</span>
      </div>
      {isReadOnly && (
        <p className="ai-assistant__completed">
          <FormattedMessage id="gritx.stepContent.aiCompleted" defaultMessage="Conversation completed."/>
        </p>
      )}
    </div>
  );
};

// ── Medication renderer ───────────────────────────────────────────────────────

const MedicationRenderer: React.FC<RendererProps> = ({content, draftPayload, onPayloadChange, isReadOnly, fieldErrors}) => {
  const indication = String(content.payload.indication_markdown ?? '');
  const dose = String(content.payload.dose ?? '');
  const frequency = String(content.payload.frequency ?? '');
  const instructions = String(content.payload.instructions ?? '');
  const acknowledged = Boolean(draftPayload.acknowledged);

  return (
    <div className="step-renderer step-renderer--medication">
      <div className="medication-card">
        {dose && <p className="medication-card__row"><strong>Dose:</strong> {dose}</p>}
        {frequency && <p className="medication-card__row"><strong>Frequency:</strong> {frequency}</p>}
        {instructions && <p className="medication-card__row">{instructions}</p>}
        {indication && <pre className="medication-card__indication">{indication}</pre>}
      </div>

      <label className={`medication-ack${fieldErrors.acknowledged ? ' medication-ack--error' : ''}`}>
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => !isReadOnly && onPayloadChange({...draftPayload, acknowledged: e.target.checked})}
          disabled={isReadOnly}
        />
        <FormattedMessage
          id="gritx.stepContent.medicationAck"
          defaultMessage="I understand my medication instructions"
        />
      </label>
      {fieldErrors.acknowledged && (
        <span className="form-question__error" role="alert">{fieldErrors.acknowledged}</span>
      )}
    </div>
  );
};

// ── Xpedition renderer ────────────────────────────────────────────────────────

const XpeditionRenderer: React.FC<RendererProps> = ({content, isReadOnly, apId}) => {
  const xpeditionId = content.payload.xpedition_id;

  return (
    <div className="step-renderer step-renderer--xpedition">
      <p className="xpedition-step__description">
        <FormattedMessage
          id="gritx.stepContent.xpeditionIntro"
          defaultMessage="Complete this step by finishing the xpedition below."
        />
      </p>
      {xpeditionId != null && !isReadOnly && (
        <Link
          className="xpedition-step__link btn btn--primary"
          to={`/xpedition/${xpeditionId}?ap_id=${apId}`}
        >
          <FormattedMessage id="gritx.stepContent.openXpedition" defaultMessage="Open Xpedition"/>
        </Link>
      )}
      {isReadOnly && (
        <p className="xpedition-step__completed">
          <FormattedMessage id="gritx.stepContent.xpeditionCompleted" defaultMessage="Xpedition completed."/>
        </p>
      )}
    </div>
  );
};

// ── Renderer registry ─────────────────────────────────────────────────────────

const RENDERER_REGISTRY: Record<StepContentType, React.FC<RendererProps>> = {
  form: FormRenderer,
  lesson: LessonRenderer,
  activity: ActivityRenderer,
  education: EducationRenderer,
  ai_assistant: AiAssistantRenderer,
  medication: MedicationRenderer,
  xpedition: XpeditionRenderer
};

// ── Retry helper ──────────────────────────────────────────────────────────────

const sleep = (ms: number) => new Promise<void>(res => { setTimeout(res, ms); });

// ── Main page ─────────────────────────────────────────────────────────────────

type StepParams = {apId: string; stepId: string};

export const StepContent: React.FC = () => {
  const params = useParams<StepParams>();
  const apId = Number(params.apId);
  const stepId = Number(params.stepId);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();

  // UCSF-3496: a Recurring/Multi-instance module's item is addressed by a step_item_progress_id —
  // ProgramDetail's item-click passes stepKind=sip as a query param to distinguish it from a PAS id.
  const [searchParams] = useSearchParams();
  const stepKindParam = searchParams.get('stepKind');
  const stepKind: StepKind | undefined = stepKindParam === 'sip' ? 'sip' : undefined;

  const [draftPayload, setDraftPayload] = useState<Record<string, unknown>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const {loading} = useSelector((state: IStore) => state.status);
  const assistantActive = useSelector((state: IStore) => state.intervention.assistantActive);
  const stepData = useSelector(
    (state: IStore) => state.intervention.stepContent?.[apId]?.[stepId],
    shallowEqual
  );
  const programDetail = useSelector((state: IStore) => state.intervention.programDetail[apId], shallowEqual);

  // Load step content
  useEffect(() => {
    if (!apId || !stepId) {
      return;
    }
    dispatch(interventionAsyncActions[getStepContent](apId, stepId, stepKind));
  }, [apId, stepId, stepKind, dispatch]);

  // Ensure program name is always available (browser back/forward may skip ProgramDetail)
  useEffect(() => {
    if (apId && !programDetail) {
      dispatch(interventionAsyncActions[getProgramDetail](apId));
    }
  }, [apId, programDetail, dispatch]);

  // Load draft on mount
  useEffect(() => {
    if (!apId || !stepId) {
      return;
    }
    getStepDraftApi(apId, stepId).then(({data}) => {
      if (data) {
        setDraftPayload(data.payload);
      }
    }).catch(() => {
      // Non-critical — proceed without draft
    });
  }, [apId, stepId]);

  // Auto-save draft every 5s when dirty (ai_assistant exempt)
  const draftRef = useRef(draftPayload);

  useEffect(() => {
    draftRef.current = draftPayload;
  }, [draftPayload]);

  useEffect(() => {
    if (!isDirty || !stepData || stepData.status === 'completed' || stepData.content.contentType === 'ai_assistant') {
      return undefined;
    }
    const {contentId} = stepData.content;
    const intervalId = setInterval(() => {
      saveStepDraftApi(apId, stepId, {contentId, payload: draftRef.current}, stepKind).catch(() => {
        // Non-critical silent failure
      });
    }, 5_000);

    return () => clearInterval(intervalId);
  }, [isDirty, apId, stepId, stepData, stepKind]);

  // SSE: forced navigation back to program
  const handleForceReturn = useCallback((reason: string) => {
    if (reason === 'step_skipped') {
      toast.info(
        intl.formatMessage({
          id: 'gritx.stepContent.skipped',
          defaultMessage: 'This step was skipped by your care team'
        })
      );
    }
    navigate(`/interventions/${apId}`, {replace: true});
  }, [apId, navigate, intl]);

  useStepContentSse(apId, stepId, handleForceReturn);

  // Back — save draft then navigate
  const handleBack = useCallback(async () => {
    if (isDirty && stepData && stepData.status !== 'completed' && stepData.content.contentType !== 'ai_assistant') {
      saveStepDraftApi(apId, stepId, {
        contentId: stepData.content.contentId,
        payload: draftPayload
      }, stepKind).catch(() => {
        // Non-critical
      });
    }
    navigate(`/interventions/${apId}`);
  }, [apId, stepId, isDirty, draftPayload, stepData, stepKind, navigate]);

  // Submit with retry (recursive, avoids await-in-loop)
  const handleSubmit = useCallback(async () => {
    if (!stepData) {
      return;
    }
    const {contentId, contentType, payload: contentPayload} = stepData.content;
    const errors = validatePayload(contentType, draftPayload, contentPayload);

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);

      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);

    const submitPayload = buildSubmitPayload(contentType, draftPayload);

    const attemptSubmit = async (attempt: number): Promise<void> => {
      try {
        const {data} = await completeStepApi(apId, stepId, {
          contentId,
          payload: submitPayload,
          clientTs: new Date().toISOString()
        }, stepKind);

        if (data.step?.stepId) {
          navigate(`/interventions/${apId}/steps/${data.step.stepId}`);
        } else {
          navigate(`/interventions/${apId}`);
        }
      } catch (err: unknown) {
        const axiosErr = err as {response?: {status?: number; data?: {errors?: unknown}}};

        if (axiosErr?.response?.status === 422) {
          const rawErrors = axiosErr.response.data?.errors;
          const serverErrors = Array.isArray(rawErrors)
            ? (rawErrors as Array<{field: string; message: string}>)
              .reduce<Record<string, string>>((acc, e) => ({...acc, [e.field]: e.message}), {})
            : {};

          setFieldErrors(serverErrors);
          setIsSubmitting(false);

          return;
        }

        if (attempt >= 2) {
          toast.error(
            intl.formatMessage({
              id: 'gritx.stepContent.submitError',
              defaultMessage: 'We couldn\'t save your answers — try again'
            })
          );
          setIsSubmitting(false);

          return;
        }

        await sleep(1_000 * (attempt + 1));
        await attemptSubmit(attempt + 1);
      }
    };

    await attemptSubmit(0);
  }, [apId, stepId, stepData, draftPayload, stepKind, navigate, intl]);

  const handlePayloadChange = useCallback((payload: Record<string, unknown>) => {
    setDraftPayload(payload);
    setIsDirty(true);
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  if (!stepData) {
    if (loading.includes(getStepContent)) {
      return <div className="step-content"><Loader nested/></div>;
    }

    return (
      <div className="step-content">
        <header className="program-header">
          <h1 className="interventions__title">
            <Link to={`/interventions/${apId}`} className="program-detail__back" aria-label="Back">‹</Link>
            {programDetail?.intervention?.name}
          </h1>
        </header>
        <div className="step-content__unavailable">
          <p>
            <FormattedMessage
              id="gritx.stepContent.unavailable"
              defaultMessage="This content is no longer available."
            />
          </p>
        </div>
      </div>
    );
  }

  const {content, moduleTitle, position, itemCount, status, completedAt} = stepData;
  const isReadOnly = status === 'completed';
  const Renderer = RENDERER_REGISTRY[content.contentType] ?? null;
  const submitLabel = SUBMIT_LABELS[content.contentType] ?? 'Submit';
  const formatDate = (iso: string | null): string => {
    if (!iso) {
      return '';
    }

    return new Date(iso).toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'});
  };

  return (
    <div className="step-content">
      <header className="program-header">
        <h1 className="interventions__title">
          <button className="step-content__back-btn" onClick={handleBack} aria-label="Back">‹</button>
          {moduleTitle}
        </h1>
        <div className="step-content__title-row">
          <h2 className="step-content__title">{content.title}</h2>
          <span className="step-content__step-counter">
            <FormattedMessage
              id="gritx.stepContent.stepOf"
              defaultMessage="Step {n} of {total}"
              values={{n: position, total: itemCount}}
            />
          </span>
        </div>
        {content.estimatedMinutes != null && (
          <p className="step-content__duration">
            <FormattedMessage
              id="gritx.stepContent.duration"
              defaultMessage="~{n} min"
              values={{n: content.estimatedMinutes}}
            />
          </p>
        )}
      </header>

      {isReadOnly && (
        <div className="step-content__completed-banner" role="status">
          <FormattedMessage
            id="gritx.stepContent.completedOn"
            defaultMessage="Completed {date}"
            values={{date: formatDate(completedAt)}}
          />
          <span className="step-content__review-pill">
            <FormattedMessage id="gritx.stepContent.reviewOnly" defaultMessage="Review only"/>
          </span>
        </div>
      )}

      <div className="step-content__renderer-card">
        {Renderer
          ? (
            <Renderer
              content={content}
              draftPayload={draftPayload}
              onPayloadChange={handlePayloadChange}
              isReadOnly={isReadOnly}
              fieldErrors={fieldErrors}
              apId={apId}
            />
          )
          : (
            <p className="step-content__unknown-type">
              <FormattedMessage
                id="gritx.stepContent.unknownType"
                defaultMessage="This content type is not yet supported."
              />
            </p>
          )
        }
      </div>

      <footer className="step-content__footer">
        <button
          className="step-content__btn step-content__btn--back"
          onClick={handleBack}
          disabled={isSubmitting}
        >
          <FormattedMessage id="gritx.stepContent.backBtn" defaultMessage="‹ Back"/>
        </button>
        {!isReadOnly && (
          <button
            className="step-content__btn step-content__btn--submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? <FormattedMessage id="gritx.stepContent.saving" defaultMessage="Saving…"/>
              : <>{submitLabel} &#8250;</>
            }
          </button>
        )}
      </footer>

      {assistantActive && (
        <button
          className="interventions__assistant-fab"
          aria-label={intl.formatMessage({id: 'gritx.interventions.assistant', defaultMessage: 'AI Assistant'})}
          onClick={() => navigate(`${RoutesUrl.Chat}?ap_id=${apId}`)}
        >
          <FormattedMessage id="gritx.interventions.assistant" defaultMessage="AI Assistant"/>
        </button>
      )}
    </div>
  );
};
